import React, { FC, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useMtsModal } from '@mts-ds/components-react';

import { Dispatch, iRootState } from '../../../../store';
import { paths, guids } from '../../../Root/paths';
import { IEmployeesList, IUserEmployees, PresenceStatus } from '../../../../models/types';
import { useAsync, useRequestHandler, useParams } from '../../../../hooks';
import { IGetAllParams } from '../../../../services/UsersService';
import isSatisfied from '../../../../utils/isSatisfied';
import { formatPresenceTime, PRESENCE_STATUS_LABEL } from '../../../../utils/employeePresence';

import Pagination from '../../../../components_/Pagination';
import EmptyState from '../../../../components_/EmptyState';
import Modal from '../../../../components_/Modal';

import Filter from './Filter';
import PresenceStatusCell from './PresenceStatusCell';
import {
  Table,
  Checkbox,
  Button,
  Popover,
  Col,
  Row,
  Space,
  Progress,
  UploadFile,
  UploadProps,
  TableProps,
  Tooltip,
  Alert
} from 'antd';
import { DeleteOutlined, EditOutlined, SettingOutlined } from '@ant-design/icons';
import Link from 'antd/lib/typography/Link';
import moment from 'moment';

import { Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { renameDepartmentsPropertiesForTable } from '../../../../utils/renameDepartmentsProperties';
import { saveAs } from 'file-saver';

const { Dragger } = Upload;

const PRESENCE_REFRESH_MS = 10000;

const initialFilter: Omit<IGetAllParams, 'pageIndex'> & { presenceStatus?: string } = {
  fullTextSearch: '',
  departmentId: undefined,
  device: undefined,
  lastPosition: undefined,
  roles: undefined,
  posts: undefined,
  presenceStatus: undefined
};

const mapState = (state: iRootState) => ({
  list: state.staffUsers.list,
  data: state.staffUsers.data,
  importResult: state.staffUsers.importResult,
  rights: state.securable.data,
  presenceFailed: state.staffUsers.list.presenceFailed
});

const mapDispatch = (dispatch: any) => {
  const d = dispatch as Dispatch;

  return {
    getList: d.staffUsers.getList,
    getData: d.staffUsers.getData,
    remove: d.staffUsers.remove,
    reset: d.staffUsers.reset,
    exportList: d.staffUsers.exportList,
    importList: d.staffUsers.importList,
    refreshPresence: d.staffUsers.refreshPresence
  };
};

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export type IProps = connectedProps;

const Index: FC<IProps> = ({
  list: { data: list, pagesCount },
  data,
  getData,
  getList,
  remove,
  reset,
  exportList,
  importList,
  importResult,
  rights,
  refreshPresence,
  presenceFailed
}) => {
  const [active, setActive] = useState<IUserEmployees | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());

  const history = useHistory();
  const requestHandler = useRequestHandler();

  const [runGetData, isLoadingGetData, isGetDataLoadingError] = useAsync(getData);
  const [runGetList, isLoadingGetList, isGetListLoadingError] = useAsync(getList);
  const [runExportList, isLoadingExportList] = useAsync(exportList);
  const {
    pageIndex,
    filter,
    handlePageChange,
    handleFilterChange,
    handleFilterReset,
    handleTableFilterChange
  } = useParams(initialFilter, runGetList);
  const { departmentId, device, lastPosition, roles, posts, presenceStatus } = filter as typeof initialFilter;

  const roleMap: Record<string, string> = data.roles.reduce(
    (acc: Record<string, string>, roleItem: any) => {
      acc[roleItem.name] = roleItem.friendlyName;
      return acc;
    },
    {}
  );

  const [fileListArray, setFileList] = useState<UploadFile[]>([]);
  const [progress, setProgress] = useState(0);

  const updateList = () => {
    getList({ ...filter, pageIndex });
  };

  const [openDeleteModal] = useMtsModal(Modal, {
    header: 'Удалить сотрудника',
    text: 'Вы действительно хотите удалить сотрудника?',
    buttonTextConfirm: 'Удалить',
    ...(active && {
      onMtsModalConfirm: async () => {
        const error = await remove(active.id);

        requestHandler({
          error,
          entity: 'user',
          isDelete: true,
          onSuccess: updateList
        });
      }
    })
  });

  const props: UploadProps = {
    accept: '.xls, .xlsx',
    multiple: false,
    maxCount: 1,
    onRemove: file => {
      const index = fileListArray.indexOf(file);
      const newFileList = fileListArray.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    onChange: async ({ fileList }) => {
      setFileList(fileList);
    },
    customRequest: async requestOptions => {
      const { onSuccess, onError } = requestOptions as any;
      const rawFile = (requestOptions as any).file as unknown;

      let uploadFile: File | null = null;
      if (typeof rawFile !== 'string') {
        if (rawFile instanceof File) {
          uploadFile = rawFile;
        } else if (rawFile instanceof Blob) {
          const name = (rawFile as any).name || 'upload.xlsx';
          uploadFile = new File([rawFile], name, {
            type: (rawFile as Blob).type || 'application/octet-stream'
          });
        }
      }

      setProgress(50);
      const error = await importList(uploadFile);
      setProgress(100);

      if (error) {
        onError?.({ error });
        setProgress(0);
      } else {
        onSuccess?.('Ok');
        setTimeout(() => setProgress(0), 1000);
      }

      requestHandler({
        error,
        entity: 'user',
        isCreate: true
      });
    },
    fileList: fileListArray
  };

  const [openImportModal] = useMtsModal(Modal, {
    header: 'Загрузить файл',
    text: '',
    buttonTextCancel: 'Закрыть',
    body: (
      <Dragger {...props}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        {progress > 0 ? (
          <Progress percent={progress} />
        ) : fileListArray[0] && fileListArray[0].status && fileListArray[0].status === 'error' ? (
          <p className="ant-upload-text" style={{ color: 'red' }}>
            Ошибка:{' '}
            {(fileListArray[0].error.error.response &&
              ` ${fileListArray[0].error.error.response.data.detail}`) ||
              fileListArray[0].error.error.message}
          </p>
        ) : importResult && fileListArray.length >= 1 ? (
          <p className="ant-upload-text">
            {`Загрузка завершена. `}
            <Link
              onClick={e => {
                e.stopPropagation();
                saveAs(importResult, `Персонал ${moment().format('DD.MM.YYYY HH-mm-ss')}.xls`);
              }}
            >
              {`Скачать файл`}
            </Link>
            {` c результатом импорта`}
          </p>
        ) : (
          <p className="ant-upload-text">Выберите файл со списком сотрудников для загрузки</p>
        )}
        <p className="ant-upload-hint">Формат файла: XLS, XLSX. Не более 10 Мб</p>
      </Dragger>
    )
  });

  useEffect(() => {
    runGetData();
    return () => {
      reset();
    };
  }, []);

  useEffect(() => {
    const presenceTimer = window.setInterval(() => {
      refreshPresence();
      setNowTick(Date.now());
    }, PRESENCE_REFRESH_MS);
    return () => window.clearInterval(presenceTimer);
  }, [refreshPresence]);

  const [checkedList, setCheckedList] = useState<string[]>([
    'presenceStatus',
    'fullName',
    'posts',
    'departmentId',
    'device',
    'lastContact',
    'lastPosition'
  ]);

  const handleClickCreate = () => {
    history.push(paths.staff.users.create);
  };

  const handleClickEdit = ({ id }: IUserEmployees | IEmployeesList) => () => {
    history.push(paths.staff.users.edit(id));
  };

  const handleClickDelete = (user: IUserEmployees | IEmployeesList) => () => {
    setActive(user as IUserEmployees);
    openDeleteModal();
  };

  const handleExportList = () => {
    runExportList(filter);
  };

  const handleImportList = () => () => {
    openImportModal({
      onDidDismiss: () => setFileList([])
    });
  };

  const isLoading = isLoadingGetList || isLoadingGetData;
  const isLoadingError = isGetListLoadingError || isGetDataLoadingError;

  const content = () => (
    <Checkbox.Group
      value={checkedList}
      style={{ width: '180px' }}
      onChange={value => {
        setCheckedList(value as string[]);
      }}
    >
      <Row>
        {options.map(option => (
          <Col key={option.value} span={24}>
            <Checkbox key={option.value} value={option.value}>
              {option.label}
            </Checkbox>
          </Col>
        ))}
      </Row>
    </Checkbox.Group>
  );

  const columns = [
    {
      title: 'Статус',
      dataIndex: 'presenceStatus',
      key: 'presenceStatus',
      render: (_: unknown, record: IEmployeesList) => (
        <PresenceStatusCell
          status={record.presenceStatus}
          unknown={presenceFailed && record.presenceStatus == null}
        />
      ),
      filterMultiple: false,
      filteredValue:
        presenceStatus && presenceStatus !== 'null' ? [presenceStatus] : null,
      filters: (Object.keys(PRESENCE_STATUS_LABEL) as PresenceStatus[]).map(value => ({
        value,
        text: PRESENCE_STATUS_LABEL[value]
      }))
    },
    {
      title: 'ФИО',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (_: unknown, record: IEmployeesList) => (
        <Link onClick={handleClickEdit(record)} style={{ color: '#007CFF' }}>
          {record.fullName}
        </Link>
      )
    },
    {
      title: 'Состояние',
      dataIndex: 'status',
      key: 'status',
      render: (_: unknown, record: IEmployeesList) => (
        <div>
          {record.status === 'ADDED' ? 'Действующий' : record.status === 'FIRED' ? 'Уволен' : '-'}
        </div>
      )
    },
    {
      title: 'Роль',
      dataIndex: 'role',
      key: 'roles',
      render: (_: unknown, record: IEmployeesList) => {
        const friendlyRoleName = record.role ? roleMap[record.role] : '-';
        return <div>{friendlyRoleName}</div>;
      },
      filterMultiple: false,
      filteredValue: roles && roles !== 'null' ? [roles] : null,
      filters: data.roles.map((obj: { id: any; name: any; friendlyName: any }) => {
        const { name, friendlyName } = obj;
        return {
          value: name,
          text: friendlyName
        };
      })
    },
    {
      title: 'Должность',
      dataIndex: 'post',
      key: 'posts',
      render: (_: unknown, record: IEmployeesList) => (
        <div>{record.post && record.post.name ? record.post.name : '-'}</div>
      ),
      filterMultiple: false,
      filterSearch: true,
      filteredValue: posts && posts !== 'null' ? [posts] : null,
      filters: [
        ...(data.posts?.map((p: { name: any; id: any }) => ({ text: p.name, value: String(p.id) })) ||
          []),
        { text: 'Без должности', value: '00000000-0000-0000-0000-000000000000' }
      ]
    },
    {
      title: 'Подразделение',
      dataIndex: 'department',
      key: 'departmentId',
      render: (_: unknown, record: IEmployeesList) => (
        <div>
          {record.department && record.department.rootName
            ? record.department.rootName +
              (record.department.name ? ' / ' + record.department.name : '-')
            : record.department && record.department.name
            ? record.department.name
            : '-'}
        </div>
      ),
      ellipsis: true,
      filterMode: 'tree' as const,
      filterMultiple: false,
      filteredValue: departmentId && departmentId !== 'null' ? [departmentId] : null,
      filters: renameDepartmentsPropertiesForTable(data.departments)
    },
    {
      title: 'ID устройства',
      dataIndex: 'deviceId',
      key: 'device',
      render: (_: unknown, record: IEmployeesList) => (
        <div>{record.deviceId ? record.deviceId : '-'}</div>
      ),
      filters: [
        { value: 'INCLUDED', text: 'Есть' },
        { value: 'NOT_INCLUDED', text: 'Нет' }
      ],
      filterMultiple: false,
      filteredValue: device && device !== 'null' ? [device] : null
    },
    {
      title: 'Сигнал получен',
      dataIndex: 'lastContact',
      key: 'lastContact',
      render: (_: unknown, record: IEmployeesList) => (
        <div key={nowTick}>{formatPresenceTime(record.lastContact)}</div>
      )
    },
    {
      title: 'Координаты получены',
      dataIndex: 'lastPositionTime',
      key: 'lastPosition',
      render: (_: unknown, record: IEmployeesList) => (
        <div key={nowTick}>{formatPresenceTime(record.lastPositionTime)}</div>
      ),
      filters: [
        { value: 'INCLUDED', text: 'Есть' },
        { value: 'NOT_INCLUDED', text: 'Нет' }
      ],
      filterMultiple: false,
      filteredValue: lastPosition && lastPosition !== 'null' ? [lastPosition] : null
    },
    {
      title: (
        <Popover content={content} placement="bottom" trigger="click">
          <Button shape="circle" icon={<SettingOutlined />} />
        </Popover>
      ),
      key: 'action',
      align: 'right' as const,
      width: '10%',
      render: (_: unknown, record: IEmployeesList) => (
        <Space>
          {isSatisfied({ uid: guids.staff.users.edit(), action: 'Visibility' }, rights) && (
            <Tooltip placement="top" title="Редактировать">
              <Button type={'text'} onClick={handleClickEdit(record)} icon={<EditOutlined />} />
            </Tooltip>
          )}
          {isSatisfied({ uid: guids.staff.users.edit(), action: 'Visibility' }, rights) && (
            <Tooltip placement="top" title="Удалить">
              <Button
                type={'text'}
                danger
                onClick={handleClickDelete(record)}
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  const options = columns
    .filter(({ key }) => key !== 'action')
    .map(({ key, title }) => ({
      label: typeof title === 'string' ? title : key,
      value: key
    }));

  const newColumns = columns.map(item => ({
    ...item,
    hidden: item.key !== 'action' && !checkedList.includes(item.key as string)
  }));

  const handleTableChange: TableProps<any>['onChange'] = (_, filters) => {
    handleTableFilterChange(filters);
  };
  return (
    <>
      {!isLoadingGetData && (
        <>
          <Filter
            filter={filter}
            list={list}
            data={data}
            rights={rights}
            isLoadingExportList={isLoadingExportList}
            onChange={handleFilterChange}
            onClickCreate={handleClickCreate}
            onClickExport={handleExportList}
            onClickImport={handleImportList}
            onReset={handleFilterReset}
          />
          {!isGetListLoadingError && (
            <>
              {presenceFailed && (
                <Alert
                  type="warning"
                  showIcon
                  message={
                    presenceStatus
                      ? 'Не удалось обновить статусы присутствия. Показаны последние успешные данные фильтра.'
                      : 'Не удалось загрузить статусы присутствия. Данные сотрудников показаны без статуса.'
                  }
                  style={{ marginTop: '1em' }}
                />
              )}
              <Table
                style={{ marginTop: '1em', overflow: 'auto' }}
                size="small"
                rowKey="id"
                loading={isLoading}
                pagination={false}
                columns={newColumns}
                onChange={handleTableChange}
                dataSource={!list ? [] : list}
                tableLayout={'auto'}
                locale={{ filterEmptyText: 'Нет данных' }}
              />
              <Pagination
                pageIndex={pageIndex}
                pagesCount={pagesCount}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </>
      )}
      {isLoadingError && <EmptyState variant="serverError" />}
    </>
  );
};

const withConnect = connect(mapState, mapDispatch);

export default withConnect(Index);
